"use client";

import * as React from "react";
import {
  AlertTriangle,
  CheckCircle,
  Info,
  Lightbulb,
  Shield,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { Alert, type AlertProps } from "./alert";

/**
 * Key Takeaway Component for MDX
 *
 * Displays important insights and key takeaways in a highlighted banner format.
 * Optimized for blog content where key insights should stand out from regular text.
 *
 * Features:
 * - Prominent lightbulb icon to indicate insight
 * - Highlighted "Takeaway" prefix
 * - Semantic color theming using design tokens
 * - Responsive padding and typography
 * - Accessible markup with proper roles
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Key takeaway content
 * @param {string} props.className - Additional CSS classes
 *
 * @example
 * ```mdx
 * <KeyTakeaway>
 *   If an agent's goals can be hijacked, it becomes a weapon turned against you---using its own legitimate access to cause harm.
 * </KeyTakeaway>
 * ```
 *
 * @example
 * ```mdx
 * <KeyTakeaway>
 *   Even legitimate, authorized tools become dangerous when agents use them incorrectly or under attacker influence.
 * </KeyTakeaway>
 * ```
 */

/**
 * Variants the blog actually uses, with the labels and icons declared in
 * blog/rivet/visual/key-takeaway.tsx — that module carries the intended
 * semantics but is not the one MDX renders, so its config never reached a page.
 * `info` and `success` appear in content without being in its union, so they
 * are included here.
 */
export type KeyTakeawayVariant =
  | "insight"
  | "security"
  | "warning"
  | "tip"
  | "info"
  | "success";

const VARIANTS: Record<
  KeyTakeawayVariant,
  { type: AlertProps["type"]; icon: LucideIcon; label: string }
> = {
  insight: { type: "info", icon: Lightbulb, label: "Key Insight" },
  security: { type: "success", icon: Shield, label: "Security Note" },
  warning: { type: "warning", icon: AlertTriangle, label: "Warning" },
  tip: { type: "notice", icon: Sparkles, label: "Pro Tip" },
  info: { type: "info", icon: Info, label: "Note" },
  success: { type: "success", icon: CheckCircle, label: "Takeaway" },
};

export interface KeyTakeawayProps {
  children: React.ReactNode;
  className?: string;
  /** Visual variant. Unknown values fall back to "insight". */
  variant?: KeyTakeawayVariant | string;
  /** Overrides the variant's default label. */
  title?: string;
}

/** Bare `<KeyTakeaway>` with no variant, as 11 places in the corpus use it. */
const DEFAULT = { type: "notice" as const, icon: Lightbulb, label: "Takeaway" };

export function KeyTakeaway({ children, className = "", variant, title }: KeyTakeawayProps) {
  // Additive on purpose: with no variant this renders exactly as it did before,
  // so the 11 bare usages are untouched. Unknown variants also fall back rather
  // than indexing straight in — the rivet module does the latter and would
  // throw on `info` or `success`, which the corpus passes.
  const config = variant ? (VARIANTS[variant as KeyTakeawayVariant] ?? DEFAULT) : DEFAULT;
  const label = title?.trim() || config.label;

  return (
    <Alert type={config.type} icon={config.icon} role="note" className={className}>
      <span className="font-semibold">{label}</span> {children}
    </Alert>
  );
}

KeyTakeaway.displayName = "KeyTakeaway";
